import React, { useEffect, useRef, useState } from "react";
import { IoSunny, IoSunnyOutline } from "react-icons/io5";
import MdiIcon from "../../../components/device/app/MdiIcon";
import { TRAIT_NAMES, hasTraitName } from "../../../constants/devices";

// ---------- RGB light (app/screens/CommonSwichDetails.js, RGBModesSelect.js) ----------

const EFFECTS = [
  { key: "mixed_constant", label: "Mixed Constant" },
  { key: "mixed_strobe", label: "Mixed Strobe" },
  { key: "rgb_constant", label: "RGB Constant" },
  { key: "rgb_fade", label: "RGB Fade" },
  { key: "rgb_rotate", label: "RGB Rotate" },
];
// The mixed effects take any colour; the RGB ones red, green or blue, and
// RGB Rotate none. Strobe, fade and rotate also take a duration.
const MIXED = ["mixed_constant", "mixed_strobe"];
const TIMED = ["mixed_strobe", "rgb_fade", "rgb_rotate"];
const PRIMARIES = [
  { key: "red", label: "Red", swatch: "rgb(255, 0, 0)" },
  { key: "green", label: "Green", swatch: "rgb(0, 128, 0)" },
  { key: "blue", label: "Blue", swatch: "rgb(0, 0, 255)" },
];
// The app's mood light colour before one is picked.
const DEFAULT_HEX = "f9e57b";

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

export const hasRgbControls = (device) =>
  [TRAIT_NAMES.COLOR, TRAIT_NAMES.EFFECTS, TRAIT_NAMES.BRIGHTNESS].some((name) => hasTraitName(device, name));

// The light's settings from its last state, which keeps intensities as 0-255
// and the colour as a hex value (mixed effects) or a colour name (RGB ones).
const fromLastState = (props = {}) => {
  const percent = (level, fallback) => (Number(level) > 0 ? clamp(Math.round((Number(level) / 255) * 100), 25, 100) : fallback);
  const color = typeof props.color === "string" ? props.color.toLowerCase() : "";
  const max = clamp(percent(props.maxIntensity, 100), 30, 100);
  const duration = Number(props.duration);
  return {
    effect: EFFECTS.some((effect) => effect.key === props.animation) ? props.animation : "mixed_constant",
    hex: /^[0-9a-f]{6}$/.test(color) && color !== "000000" && color !== "ffffff" ? color : DEFAULT_HEX,
    primary: PRIMARIES.some((primary) => primary.key === color) ? color : "red",
    brightness: percent(props.intensity, 50),
    max,
    min: clamp(percent(props.minIntensity, 25), 25, Math.min(75, max - 5)),
    speed: duration >= 100 && duration <= 2000 ? duration : 100,
  };
};

// The glow behind the bulb: the colour being shown, as bright as it's set.
const glowOf = ({ effect, hex, primary, brightness, max }) => {
  if (MIXED.includes(effect)) return { color: `#${hex}`, level: 1 };
  if (effect === "rgb_rotate") return { color: "conic-gradient(red, lime, blue, red)", level: brightness / 100 };
  const swatch = PRIMARIES.find((item) => item.key === primary)?.swatch;
  return { color: swatch, level: (effect === "rgb_fade" ? max : brightness) / 100 };
};

// A slider that sends its value when it's let go, like the dimmer's.
const Slider = ({ id, label, value, min, max, step = 1, unit, disabled, icons = true, onMove, onCommit }) => (
  <div className="hx-rgb-group">
    <div className="hx-rgb-label">
      <span id={`${id}-label`}>{label}</span>
      <span>
        {value} {unit}
      </span>
    </div>
    <div className="hx-rgb-slider">
      {icons ? <IoSunnyOutline aria-hidden="true" /> : null}
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        aria-labelledby={`${id}-label`}
        aria-valuetext={`${value} ${unit}`}
        onChange={(event) => onMove(Number(event.target.value))}
        onPointerUp={(event) => onCommit(Number(event.currentTarget.value))}
        onKeyUp={(event) => onCommit(Number(event.currentTarget.value))}
      />
      {icons ? <IoSunny aria-hidden="true" /> : null}
    </div>
  </div>
);

// Effect, colour and brightness for lights with the ColorSetting,
// LightEffects or Brightness trait. As in the app, colour and brightness show
// while the light is on, and any change also switches it on. onSend gets the
// settings for rgbPayload and resolves to whether the light took them.
const RgbControl = ({ device, icon, on, disabled, onSend }) => {
  const [settings, setSettings] = useState(() => fromLastState(device.props));
  // What the light last took, to go back to when a change doesn't reach it.
  const sent = useRef(settings);
  // Once something is changed here, a refreshed last state doesn't move it back.
  const touched = useRef(false);
  const colorInput = useRef(null);
  const commitHex = useRef(null);
  const props = device.props ?? {};

  useEffect(() => {
    if (touched.current) return;
    const latest = fromLastState(props);
    sent.current = latest;
    setSettings(latest);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.animation, props.color, props.intensity, props.maxIntensity, props.minIntensity, props.duration]);

  const hasColor = hasTraitName(device, TRAIT_NAMES.COLOR);
  const hasEffects = hasTraitName(device, TRAIT_NAMES.EFFECTS);
  const hasBrightness = hasTraitName(device, TRAIT_NAMES.BRIGHTNESS);
  const { effect } = settings;
  const mixed = MIXED.includes(effect);
  const showPicker = on && hasColor && mixed;
  const showPrimaries = on && hasColor && !mixed && effect !== "rgb_rotate";
  const showBrightness = on && hasBrightness && !mixed;

  // Sends the settings with one change; if the light doesn't take them, the
  // page goes back to what it last took.
  const apply = async (change) => {
    touched.current = true;
    const next = { ...settings, ...change };
    setSettings(next);
    const color = MIXED.includes(next.effect) ? next.hex : next.primary;
    if (await onSend({ ...next, color })) sent.current = next;
    else setSettings(sent.current);
  };
  commitHex.current = (hex) => apply({ hex });

  // The colour box sends once its picker closes ("change"), not while the
  // colour is being dragged ("input", which React's onChange follows).
  useEffect(() => {
    const input = colorInput.current;
    if (!showPicker || !input) return undefined;
    const commit = (event) => commitHex.current(event.target.value.slice(1).toLowerCase());
    input.addEventListener("change", commit);
    return () => input.removeEventListener("change", commit);
  }, [showPicker]);

  const move = (key) => (value) => {
    touched.current = true;
    setSettings((current) => ({ ...current, [key]: value }));
  };
  // Fade keeps its maximum at least 5 points above its minimum.
  const moveMax = (value) => move("max")(Math.max(value, settings.min + 5));
  const moveMin = (value) => move("min")(Math.min(value, settings.max - 5));

  const glow = glowOf(settings);

  return (
    <div className="hx-rgb">
      <div
        className="hx-rgb-bulb"
        style={on ? { "--hx-rgb-glow": glow.color, "--hx-rgb-level": glow.level } : undefined}
        aria-hidden="true"
      >
        <MdiIcon path={icon} size={170} />
      </div>

      {hasEffects ? (
        <div className="hx-rgb-group" role="group" aria-labelledby="hx-rgb-effect-label">
          <div className="hx-rgb-label">
            <span id="hx-rgb-effect-label">Effect</span>
          </div>
          <div className="hx-rgb-effects">
            {EFFECTS.map((item) => (
              <button
                key={item.key}
                type="button"
                className={item.key === effect ? "is-selected" : ""}
                aria-pressed={item.key === effect}
                disabled={disabled}
                onClick={() => apply({ effect: item.key })}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {hasEffects && TIMED.includes(effect) ? (
        <Slider
          id="hx-rgb-speed"
          label="Duration"
          value={settings.speed}
          min={100}
          max={2000}
          step={5}
          unit="ms"
          icons={false}
          disabled={disabled}
          onMove={move("speed")}
          onCommit={(speed) => apply({ speed })}
        />
      ) : null}

      {showPicker ? (
        <div className="hx-rgb-group">
          <div className="hx-rgb-label">
            <label htmlFor="hx-rgb-color">Colour</label>
            <span>#{settings.hex.toUpperCase()}</span>
          </div>
          <input
            ref={colorInput}
            id="hx-rgb-color"
            className="hx-rgb-picker"
            type="color"
            value={`#${settings.hex}`}
            disabled={disabled}
            onChange={(event) => move("hex")(event.target.value.slice(1).toLowerCase())}
          />
        </div>
      ) : null}

      {showPrimaries ? (
        <div className="hx-rgb-group" role="group" aria-labelledby="hx-rgb-primary-label">
          <div className="hx-rgb-label">
            <span id="hx-rgb-primary-label">Colour</span>
          </div>
          <div className="hx-rgb-colors">
            {PRIMARIES.map((item) => (
              <button
                key={item.key}
                type="button"
                className={item.key === settings.primary ? "is-selected" : ""}
                style={{ "--hx-rgb-swatch": item.swatch }}
                aria-pressed={item.key === settings.primary}
                aria-label={item.label}
                title={item.label}
                disabled={disabled}
                onClick={() => apply({ primary: item.key })}
              />
            ))}
          </div>
        </div>
      ) : null}

      {showBrightness && effect === "rgb_fade" ? (
        <>
          <Slider
            id="hx-rgb-max"
            label="Max brightness"
            value={settings.max}
            min={30}
            max={100}
            unit="%"
            disabled={disabled}
            onMove={moveMax}
            onCommit={(value) => apply({ max: Math.max(value, settings.min + 5) })}
          />
          <Slider
            id="hx-rgb-min"
            label="Min brightness"
            value={settings.min}
            min={25}
            max={75}
            unit="%"
            disabled={disabled}
            onMove={moveMin}
            onCommit={(value) => apply({ min: Math.min(value, settings.max - 5) })}
          />
        </>
      ) : null}

      {showBrightness && effect !== "rgb_fade" ? (
        <Slider
          id="hx-rgb-brightness"
          label="Brightness"
          value={settings.brightness}
          min={25}
          max={100}
          unit="%"
          disabled={disabled}
          onMove={move("brightness")}
          onCommit={(brightness) => apply({ brightness })}
        />
      ) : null}
    </div>
  );
};

export default RgbControl;
